// Conditional fields on a ticket form: "when Category is internet/networking, also ask
// Main Internet Issue".
//
// WHY IT LIVES UNDER THE FORM. A condition is about THIS form — the same field is a
// parent on one, a child on another, and on neither elsewhere — so there is nothing for
// the custom-field screen to own. Zendesk configures them on the form for the same
// reason, and that screen links here instead.
//
// Every decision that can be made without a DOM is in ../../pages/admin/form-conditions-edit.js
// and tested there. This file draws what that module returns.

import { useCallback, useEffect, useState } from 'react';

import { apiFetch } from '../../api/client.js';
import { consoleErrorMessage } from '../../pages/admin/descriptor-form.js';
import {
  anchorCandidates, valueChoicesFor, childCandidates, conditionProblems,
  describeCondition, blankCondition, toWire, fromWire,
  filterChildren, collapsedSummary,
} from '../../pages/admin/form-conditions-edit.js';

const SELECT = 'w-full rounded border border-line-strong bg-surface px-2 py-1 text-sm text-ink';

// A STABLE KEY PER DRAFT ROW, because "which rows are open" and "what is typed in each
// row's search" are held outside the row. Keyed by array index, removing the second of
// five rows would slide the third into its place and hand it the removed row's open state
// and search text — the rule the admin was reading would silently become a different one.
// Client-side only: `toWire` builds its payload from named fields, so this never ships.
let nextUid = 0;
const withUid = (condition) => ({ ...condition, uid: `c${(nextUid += 1)}` });

export default function FormConditions({ formId }) {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // WHICH ROWS ARE OPEN, not which are shut, so the default is closed and a newly added
  // row can be opened explicitly. The desk's largest form has 21 conditions over 42
  // fields — about 860 checkboxes if every row is expanded — and the useful view of that
  // page is the list of rules, not all of their contents at once.
  const [open, setOpen] = useState(() => new Set());
  // Per row. One shared search box would filter 21 rows at once, and the admin is
  // looking inside ONE rule when they type.
  const [searches, setSearches] = useState({});

  const load = useCallback((signal) => Promise.all([
    apiFetch(`/admin/ticket-forms/${encodeURIComponent(formId)}/conditions`, { signal }),
    // The form's OWN fields, not every definition: only a field the form carries can take
    // part, because a condition naming one it does not carry can never fire and its
    // children can never appear.
    apiFetch(`/admin/ticket-forms/${encodeURIComponent(formId)}/fields`, { signal }),
  ]).then(([conditions, fields]) => {
    setSaved(conditions.data);
    setDraft(fromWire(conditions.data).map(withUid));
    setFormFields(fields.data);
  }), [formId]);

  useEffect(() => {
    const controller = new AbortController();
    setSaved(null);
    setError(null);
    load(controller.signal)
      .catch((err) => { if (err?.name !== 'AbortError') setError(consoleErrorMessage(err)); });
    return () => controller.abort();
  }, [formId, load]);

  function update(index, patch) {
    setDraft((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function toggleChild(index, fieldId) {
    setDraft((rows) => rows.map((row, i) => {
      if (i !== index) return row;
      const has = row.children.some((child) => String(child.id) === String(fieldId));
      return {
        ...row,
        children: has
          ? row.children.filter((child) => String(child.id) !== String(fieldId))
          : [...row.children, { id: String(fieldId), is_required: false }],
      };
    }));
  }

  function toggleRequired(index, fieldId) {
    setDraft((rows) => rows.map((row, i) => (i === index ? {
      ...row,
      children: row.children.map((child) => (String(child.id) === String(fieldId)
        ? { ...child, is_required: !child.is_required } : child)),
    } : row)));
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const body = await apiFetch(`/admin/ticket-forms/${encodeURIComponent(formId)}/conditions`, {
        method: 'PUT',
        body: { conditions: toWire(draft) },
      });
      // The response is the list as it now stands, so there is no window in which the
      // screen and the server disagree about what was just saved.
      setSaved(body.data);
      setDraft(fromWire(body.data).map(withUid));
    } catch (err) {
      setError(consoleErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (saved === null) {
    return (
      <Shell>
        <p className="mt-2 text-sm text-ink-muted">{error ?? 'Loading…'}</p>
      </Shell>
    );
  }

  const anchors = anchorCandidates(formFields);
  const problems = draft.map((condition) => conditionProblems(condition, formFields));
  const blocked = problems.some((list) => list.length > 0);

  return (
    <Shell>
      <p className="mt-1 text-xs text-ink-secondary">
        A field named below is hidden until its condition is met. Everything else on the
        form is always shown.
      </p>

      {anchors.length === 0 && (
        <p className="mt-3 text-sm text-ink-muted">
          No field on this form can decide a condition yet. Add a dropdown, checkbox or
          text field first.
        </p>
      )}

      <ul className="mt-3 space-y-2">
        {draft.map((condition, index) => {
          const parent = formFields.find((f) => String(f.id) === String(condition.parent_field_id));
          const choices = valueChoicesFor(parent);
          const isOpen = open.has(condition.uid);
          const rowProblems = problems[index];
          return (
            <li key={condition.uid} className="rounded-lg border border-line">
              {/* THE ROW IS THE SUMMARY when shut, which is most of the time. It carries
                  the field count and the problem count because a row that cannot be saved
                  has to say so while closed -- otherwise Save is disabled for a reason
                  the admin has to go looking for. */}
              <button
                type="button"
                className="flex w-full items-start gap-2 px-3 py-2 text-left"
                onClick={() => setOpen((previous) => {
                  const next = new Set(previous);
                  if (next.has(condition.uid)) next.delete(condition.uid);
                  else next.add(condition.uid);
                  return next;
                })}
                aria-expanded={isOpen}
              >
                <span className="mt-0.5 shrink-0 text-xs text-ink-muted">
                  {isOpen ? '▾' : '▸'}
                </span>
                <span className={`flex-1 text-xs ${rowProblems.length > 0
                  ? 'text-red-700 dark:text-red-300' : 'text-ink-secondary'}`}>
                  {collapsedSummary(condition, formFields, rowProblems)}
                </span>
              </button>

              {isOpen && (
              <div className="border-t border-line p-3">
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="text-xs text-ink-secondary">
                  When this field
                  <select
                    className={`mt-1 ${SELECT}`}
                    value={condition.parent_field_id}
                    onChange={(e) => update(index, { parent_field_id: e.target.value, value: '' })}
                  >
                    <option value="">Choose a field…</option>
                    {anchors.map((field) => (
                      <option key={field.id} value={field.id}>{field.label}</option>
                    ))}
                  </select>
                </label>

                <label className="text-xs text-ink-secondary">
                  is
                  {/* A select's own choices, so the value cannot be mistyped. A typo'd
                      value is a condition that never fires, and one that never fires
                      looks exactly like one that has not been triggered yet. */}
                  {choices ? (
                    <select
                      className={`mt-1 ${SELECT}`}
                      value={condition.value}
                      onChange={(e) => update(index, { value: e.target.value })}
                    >
                      <option value="">Choose an answer…</option>
                      {choices.map((choice) => (
                        <option key={choice.value} value={choice.value}>{choice.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className={`mt-1 ${SELECT}`}
                      value={condition.value}
                      onChange={(e) => update(index, { value: e.target.value })}
                      placeholder="the exact answer"
                    />
                  )}
                </label>
              </div>

              <fieldset className="mt-3">
                <legend className="text-xs text-ink-secondary">then also ask</legend>
                {/* THE SEARCH BOX. This form's field list runs to 49, and the largest on
                    the desk pairs 42 fields with 21 conditions -- finding "Live Date" in
                    that is a scroll, and a scroll is how somebody ticks the box next to
                    the one they wanted. A ticked field is never filtered out of view,
                    whatever is typed: hiding one would leave a row reading "4 fields"
                    while showing two, and the obvious next move is to re-tick something
                    that is already ticked somewhere off screen. */}
                {childCandidates(formFields, condition.parent_field_id).length > 8 && (
                  <input
                    type="search"
                    className={`mt-1 ${SELECT}`}
                    value={searches[condition.uid] ?? ''}
                    onChange={(e) => setSearches((previous) => (
                      { ...previous, [condition.uid]: e.target.value }))}
                    placeholder="Search these fields by name or key…"
                  />
                )}
                <div className="mt-1 grid gap-1 sm:grid-cols-2">
                  {filterChildren(
                    childCandidates(formFields, condition.parent_field_id),
                    searches[condition.uid],
                    condition.children.map((child) => child.id),
                  ).map((field) => {
                    const chosen = condition.children
                      .find((child) => String(child.id) === String(field.id));
                    return (
                      <div key={field.id} className="flex items-center gap-2 text-sm text-ink">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-line-strong"
                          checked={Boolean(chosen)}
                          onChange={() => toggleChild(index, field.id)}
                        />
                        <span className="truncate" title={field.label}>{field.label}</span>
                        {chosen && (
                          <label className="ml-auto flex items-center gap-1 text-xs text-ink-secondary">
                            <input
                              type="checkbox"
                              className="h-3 w-3 rounded border-line-strong"
                              checked={chosen.is_required}
                              onChange={() => toggleRequired(index, field.id)}
                            />
                            required
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </fieldset>

              {problems[index].map((problem) => (
                <p key={problem} className="mt-1 text-xs text-red-700 dark:text-red-300">{problem}</p>
              ))}

              <button
                type="button"
                className="mt-2 text-xs text-red-700 hover:underline dark:text-red-300"
                onClick={() => setDraft((rows) => rows.filter((_, i) => i !== index))}
              >
                Remove this condition
              </button>
              </div>
              )}
            </li>
          );
        })}
      </ul>

      {draft.length === 0 && (
        <p className="mt-3 text-sm text-ink-muted">
          This form asks every one of its fields, every time.
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
        <button
          type="button"
          className="btn"
          disabled={anchors.length === 0}
          onClick={() => {
            const row = withUid(blankCondition());
            setDraft((rows) => [...rows, row]);
            // Open it. A row added shut is a row that looks like nothing happened.
            setOpen((previous) => new Set(previous).add(row.uid));
          }}
        >
          Add a condition
        </button>
        <button type="button" className="btn btn-primary" disabled={saving || blocked} onClick={save}>
          {saving ? 'Saving…' : 'Save conditions'}
        </button>
        {blocked && (
          <span className="text-xs text-ink-secondary">
            Fix the problems above first — a condition that cannot fire hides its fields for ever.
          </span>
        )}
        {error && <span className="text-xs text-red-700 dark:text-red-300">{error}</span>}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <section className="mt-6 rounded-lg border border-line bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink">Conditional fields</h2>
      {children}
    </section>
  );
}
